import React, { useCallback, useEffect, useState } from 'react';
import { Check, Loader2, Plus, ShieldCheck, Trash2, TriangleAlert } from 'lucide-react';

import { assistantsService, type CanalWhatsApp as Canal } from '../../services/api/assistants';

/**
 * Rattachement des numéros et portée du canal WhatsApp.
 *
 * Le canal ne vaut que par l'association numéro ↔ compte : un message venu d'un
 * numéro inconnu ne désigne personne, donc ni droits à appliquer, ni dossiers à
 * ouvrir, ni consommation à imputer. Un numéro rattaché et confirmé, à
 * l'inverse, est un agent identifié — l'échange arrivé par téléphone est alors
 * un échange comme les autres.
 *
 * La seconde moitié de l'écran décide de ce qui passe par là. Tout n'a pas
 * vocation à être joignable : un budget se corrige ligne à ligne avant export,
 * cela ne se fait pas au téléphone.
 */
const CanalWhatsApp: React.FC = () => {
  const [canal, setCanal] = useState<Canal | null>(null);
  const [erreur, setErreur] = useState('');
  const [occupe, setOccupe] = useState(false);
  const [compte, setCompte] = useState('');
  const [numero, setNumero] = useState('');
  // Code affiché à l'administrateur tant que la passerelle n'est pas en
  // service : c'est lui qui le transmet de vive voix à l'agent.
  const [aTransmettre, setATransmettre] = useState<{ id: number; code: string } | null>(null);
  const [saisieCode, setSaisieCode] = useState('');
  const [qr, setQr] = useState<string | null>(null);
  // Le code est-il parti par WhatsApp, ou faut-il le dicter ?
  const [envoye, setEnvoye] = useState(false);
  // Code saisi par l'administrateur, par numéro en attente.
  const [codes, setCodes] = useState<Record<number, string>>({});

  const recharger = useCallback(async () => {
    try {
      setCanal(await assistantsService.chargerCanalWhatsApp());
    } catch (e: any) {
      setErreur(e.message || 'Chargement impossible.');
    }
  }, []);

  useEffect(() => {
    recharger();
  }, [recharger]);

  // Le code de pairage n'est valable que quelques dizaines de secondes : on le
  // recharge tant que la session n'est pas appairée.
  useEffect(() => {
    if (!canal?.session.qr_disponible || canal.session.appairee) {
      setQr(null);
      return;
    }
    let vivant = true;
    let url: string | null = null;
    const recuperer = async () => {
      try {
        const blob = await assistantsService.qrWhatsApp();
        if (!vivant) return;
        if (url) URL.revokeObjectURL(url);
        url = URL.createObjectURL(blob);
        setQr(url);
      } catch {
        if (vivant) setQr(null);
      }
    };
    recuperer();
    const minuteur = setInterval(() => { recuperer(); recharger(); }, 15000);
    return () => {
      vivant = false;
      clearInterval(minuteur);
      if (url) URL.revokeObjectURL(url);
    };
  }, [canal?.session.qr_disponible, canal?.session.appairee, recharger]);

  const agir = async (action: () => Promise<unknown>) => {
    setOccupe(true);
    setErreur('');
    try {
      await action();
      await recharger();
    } catch (e: any) {
      setErreur(e.message || 'Opération impossible.');
    } finally {
      setOccupe(false);
    }
  };

  const rattacher = async () => {
    if (!compte || !numero.trim()) return;
    setOccupe(true);
    setErreur('');
    try {
      const cree = await assistantsService.rattacherNumero(Number(compte), numero.trim());
      setATransmettre({ id: cree.id, code: cree.code });
      setEnvoye(cree.envoye);
      setNumero('');
      setCompte('');
      await recharger();
    } catch (e: any) {
      setErreur(e.message || 'Rattachement impossible.');
    } finally {
      setOccupe(false);
    }
  };

  if (!canal) {
    return (
      <div className="flex items-center gap-2 py-12 text-sm text-eg-gris">
        <Loader2 className="h-4 w-4 animate-spin" />
        Chargement du canal...
      </div>
    );
  }

  const ouverts = canal.assistants.filter((a) => a.ouvert).length;

  return (
    <div className="space-y-4">
      <section className="overflow-hidden rounded-md border border-[#DDE2DD] bg-eg-blanc">
        <header className="flex items-baseline gap-2.5 border-b border-eg-bordure px-3 py-2.5">
          <h2 className="text-[13px] font-semibold text-eg-encre">Téléphone émetteur</h2>
          <span className="text-[11px] text-eg-gris">
            le téléphone qui scanne devient l'émetteur du service
          </span>
        </header>

        {!canal.session.disponible ? (
          <div className="flex items-start gap-2.5 bg-[#EEF2ED] px-4 py-3">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-eg-alerte" />
            <p className="text-[12.5px] leading-relaxed text-eg-gris">
              <span className="font-semibold text-eg-encre">
                La passerelle n'est pas démarrée.
              </span>{' '}
              {canal.session.detail} L'annuaire ci-dessous reste utilisable : il vaut
              quelle que soit la passerelle retenue ensuite.
            </p>
          </div>
        ) : canal.session.appairee ? (
          <div className="flex flex-wrap items-center gap-3 px-4 py-3">
            <span className="inline-flex items-center gap-1.5 rounded bg-eg-valide/10 px-2 py-1
              text-[11px] font-semibold text-eg-valide">
              <ShieldCheck className="h-3.5 w-3.5" /> session active
            </span>
            <span className="text-[12.5px] text-eg-gris">
              Émission depuis le{' '}
              <span className="font-semibold tabular-nums text-eg-encre">
                {canal.session.numero_emetteur || 'numéro appairé'}
              </span>
            </span>
            <button
              type="button"
              onClick={() => agir(() => assistantsService.relancerWhatsApp())}
              disabled={occupe}
              className="ml-auto rounded-[5px] border border-eg-bord bg-eg-blanc px-3 py-1.5
                text-[12px] font-semibold text-eg-brun hover:bg-eg-papier disabled:opacity-40"
            >
              Dissocier et réappairer
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap items-start gap-5 px-4 py-4">
            <div className="shrink-0 rounded-md border border-eg-bord bg-eg-blanc p-2">
              {qr ? (
                <img src={qr} alt="Code à scanner" className="h-44 w-44" />
              ) : (
                <div className="flex h-44 w-44 items-center justify-center text-[11px] text-eg-estompe">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> code en préparation
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1 space-y-1.5 text-[12.5px] text-eg-gris">
              <p className="font-semibold text-eg-encre">Appairer le téléphone du service</p>
              <p>1. Sur le téléphone dédié, ouvrir WhatsApp.</p>
              <p>2. Menu <span className="text-eg-encre">Appareils connectés</span>,
                puis <span className="text-eg-encre">Connecter un appareil</span>.</p>
              <p>3. Scanner ce code.</p>
              <p className="pt-1.5 text-[11.5px] text-eg-estompe">
                Le code se renouvelle toutes les quinze secondes. Utilisez un numéro
                dédié au service, jamais un numéro personnel : c'est ce compte WhatsApp
                qui portera tous les échanges.
              </p>
            </div>
          </div>
        )}
      </section>

      {erreur && (
        <p className="rounded-md border border-eg-accent/30 bg-eg-accent/10 px-3 py-2 text-sm text-eg-accent">
          {erreur}
        </p>
      )}

      <section className="overflow-hidden rounded-md border border-[#DDE2DD] bg-eg-blanc">
        <header className="flex items-baseline gap-2.5 border-b border-eg-bordure px-3 py-2.5">
          <h2 className="text-[13px] font-semibold text-eg-encre">Numéros rattachés</h2>
          <span className="text-[11px] text-eg-gris">
            {canal.numeros.length} numéro{canal.numeros.length > 1 ? 's' : ''} ·{' '}
            {canal.numeros.filter((n) => n.verifie).length} confirmé
            {canal.numeros.filter((n) => n.verifie).length > 1 ? 's' : ''}
          </span>
        </header>

        <table className="w-full text-[12.5px]">
          <thead className="bg-eg-papier text-[10px] font-semibold uppercase tracking-[0.04em] text-eg-gris">
            <tr>
              <th className="px-3 py-1.5 text-left">Numéro</th>
              <th className="px-3 py-1.5 text-left">Titulaire</th>
              <th className="px-3 py-1.5 text-left">État</th>
              <th className="px-3 py-1.5" />
            </tr>
          </thead>
          <tbody>
            {canal.numeros.map((n) => (
              <tr key={n.id} className="border-t border-eg-voile">
                <td className="px-3 py-2 tabular-nums text-eg-encre">{n.numero}</td>
                <td className="px-3 py-2">
                  <div className="text-eg-encre">{n.utilisateur}</div>
                  <div className="text-[10.5px] text-eg-estompe">{n.email}</div>
                </td>
                <td className="px-3 py-2">
                  {n.verifie ? (
                    <span className="inline-flex items-center gap-1 rounded bg-eg-valide/10 px-1.5
                      py-0.5 text-[10.5px] font-semibold text-eg-valide">
                      <ShieldCheck className="h-3 w-3" /> confirmé
                    </span>
                  ) : (
                    // La confirmation se fait ici, sur la ligne du numéro : le
                    // champ ne doit pas disparaître dès qu'on quitte l'écran.
                    <div className="flex flex-wrap items-center gap-1.5">
                      <input
                        value={codes[n.id] || ''}
                        onChange={(e) =>
                          setCodes((p) => ({ ...p, [n.id]: e.target.value }))
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && (codes[n.id] || '').trim()) {
                            agir(() =>
                              assistantsService.confirmerNumero(
                                n.id, (codes[n.id] || '').trim(),
                              ),
                            );
                          }
                        }}
                        disabled={occupe}
                        placeholder="code reçu"
                        inputMode="numeric"
                        maxLength={6}
                        className="w-24 rounded border border-eg-alerte/50 bg-eg-blanc px-2 py-1
                          text-[12px] tracking-[0.15em] focus:border-eg-accent focus:outline-none
                          disabled:opacity-50"
                      />
                      <button
                        type="button"
                        disabled={occupe || !(codes[n.id] || '').trim()}
                        onClick={() =>
                          agir(() =>
                            assistantsService.confirmerNumero(
                              n.id, (codes[n.id] || '').trim(),
                            ),
                          )
                        }
                        className="rounded p-1 text-eg-valide hover:bg-eg-valide/10
                          disabled:opacity-30"
                        title="Confirmer ce numéro"
                      >
                        <Check className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        disabled={occupe}
                        onClick={() =>
                          agir(async () => {
                            const suite = await assistantsService.renvoyerCode(n.id);
                            setATransmettre({ id: n.id, code: suite.code });
                            setEnvoye(suite.envoye);
                          })
                        }
                        className="text-[10.5px] text-eg-gris underline hover:text-eg-encre
                          disabled:opacity-40"
                      >
                        renvoyer
                      </button>
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => agir(() => assistantsService.retirerNumero(n.id))}
                    disabled={occupe}
                    title="Retirer ce rattachement"
                    className="rounded p-1 text-eg-estompe hover:bg-eg-accent/10
                      hover:text-eg-accent disabled:opacity-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
            {canal.numeros.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-3 text-eg-gris">
                  Aucun numéro rattaché.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="flex flex-wrap items-center gap-2 border-t border-eg-bordure bg-eg-papier px-3 py-2.5">
          <select
            value={compte}
            onChange={(e) => setCompte(e.target.value)}
            disabled={occupe}
            className="rounded-md border border-[#DDE2DD] bg-eg-blanc px-2 py-1.5 text-[12.5px]
              focus:border-eg-accent focus:outline-none disabled:opacity-50"
          >
            <option value="">Choisir un agent...</option>
            {canal.candidats.map((c) => (
              <option key={c.id} value={c.id}>{c.nom} — {c.email}</option>
            ))}
          </select>
          <input
            value={numero}
            onChange={(e) => setNumero(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && rattacher()}
            disabled={occupe}
            placeholder="621 00 00 00"
            className="w-44 rounded-md border border-[#DDE2DD] bg-eg-blanc px-2 py-1.5
              text-[12.5px] focus:border-eg-accent focus:outline-none disabled:opacity-50"
          />
          <button
            type="button"
            onClick={rattacher}
            disabled={occupe || !compte || !numero.trim()}
            className="inline-flex items-center gap-1.5 rounded-[5px] bg-eg-ombre px-3 py-1.5
              text-[12.5px] font-semibold text-eg-blanc hover:bg-eg-encre disabled:opacity-40"
          >
            {occupe ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Rattacher
          </button>
        </div>

        {aTransmettre && (
          <div className="border-t border-eg-bordure bg-[#EEF2ED] px-3 py-2.5">
            <p className="text-[12.5px] text-eg-gris">
              {envoye
                ? "Code envoyé par WhatsApp au titulaire. Il le lit sur son téléphone et vous le dicte — vous le saisissez sur la ligne du numéro ci-dessus."
                : "La passerelle n'émet pas : transmettez ce code de vive voix, puis saisissez-le sur la ligne du numéro ci-dessus."}
            </p>
            <p className="mt-1 font-mono text-[15px] font-bold tracking-[0.2em] text-eg-encre">
              {aTransmettre.code}
            </p>
          </div>
        )}
      </section>

      <section className="overflow-hidden rounded-md border border-[#DDE2DD] bg-eg-blanc">
        <header className="flex items-baseline gap-2.5 border-b border-eg-bordure px-3 py-2.5">
          <h2 className="text-[13px] font-semibold text-eg-encre">Assistants joignables</h2>
          <span className="text-[11px] text-eg-gris">
            {ouverts} ouvert{ouverts > 1 ? 's' : ''} sur {canal.assistants.length}
          </span>
        </header>
        <p className="border-b border-eg-bordure px-3 py-2 text-[12px] text-eg-gris">
          Le canal sert à consulter depuis le terrain. Un document officiel se corrige
          avant export : cela ne se fait pas au téléphone.
        </p>

        <div className="max-h-80 overflow-y-auto">
          {canal.assistants.map((a) => (
            <label
              key={a.id}
              className="flex cursor-pointer items-center gap-2.5 border-t border-eg-voile
                px-3 py-2 text-[12.5px] hover:bg-[#EEF2ED]"
            >
              <input
                type="checkbox"
                checked={a.ouvert}
                disabled={occupe}
                onChange={(e) =>
                  agir(() => assistantsService.ouvrirAuCanal(a.id, e.target.checked))
                }
                className="rounded border-[#DDE2DD] text-eg-accent focus:ring-eg-accent"
              />
              <span className="min-w-0 flex-1 truncate text-eg-encre">{a.titre}</span>
              <span className="shrink-0 text-[10px] uppercase tracking-wide text-eg-estompe">
                {a.format}
              </span>
            </label>
          ))}
        </div>
      </section>
    </div>
  );
};

export default CanalWhatsApp;
